import { Box, Heading, Table, Text } from "@chakra-ui/react";
import _ from "lodash";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

import {
  checkServiceHasChildren,
  generateHoursArray,
  formatTime,
  convertTo24HourHour,
} from "../../../../store/helpers";

import AgentServiceTimePerServiceLineGraph from "../../../branches/charts/daily/AgentServiceTimePerServiceLineGraph";
import { TICKET_STATUS } from "../../../../store/constants";
import EnterpriseReportSummary from "../../../reports/EnterpriseReportSummary";
import { buildRealtimeDurationSummary } from "../../../reports/RealtimeEnterpriseSummary";

const OrganizationAverageWaitTimePerService = () => {
  const { workDay, branches, servicesSubscriptions, services } = useSelector(
    (state) => state.defaultReducer
  );
  const { tickets, timeNow } = useSelector((state) => state.realtimeReducer);
  const ticketsClone = _.cloneDeep(tickets);
  const branchesClone = _.cloneDeep(branches).filter(
    (a) => a.reportStatus != null && Number(a.reportStatus)
  );
  const servicesSubscriptionsClone = _.cloneDeep(servicesSubscriptions);
  const servicesClone = _.cloneDeep(services);

  // Get all leaf services across the org
  const allServiceIds = new Set();
  branchesClone.forEach((branch) => {
    const branchServices =
      servicesSubscriptionsClone.find((s) => s.branchId === branch.id)
        ?.services || [];
    branchServices.forEach((id) => allServiceIds.add(id));
  });

  const leafServices = checkServiceHasChildren(servicesClone).filter(
    (s) => allServiceIds.has(s.id) && !s.hasChildren
  );

  const startTimeStamp = dayjs()
    .set("hour", workDay[0].split(" ")[0])
    .set("minute", 0)
    .unix();
  const endTimeStamp = timeNow;
  const hoursArray = generateHoursArray(startTimeStamp, endTimeStamp);

  const servicesData = leafServices
    .map((service) => {
      const serviceTickets = ticketsClone.filter(
        (t) => t.serviceId === service.id
      );

      const chart = hoursArray
        .map((hour) => {
          if (!workDay.includes(hour)) return null;

          const time24 = convertTo24HourHour(hour);

          const incompletedTickets = serviceTickets
            .filter((a) => a.status !== TICKET_STATUS.COMPLETED)
            .filter((a) => Number(dayjs.unix(a.created).hour()) <= time24);

          const currentTime =
            dayjs().hour() === time24
              ? dayjs().unix()
              : dayjs().set("hour", time24).set("minute", 59).unix();

          const incompleteWaitTime =
            incompletedTickets.reduce(
              (acc, a) => acc + (currentTime - a.created),
              0
            ) || 0;

          const completedTickets = serviceTickets.filter(
            (a) =>
              Number(a.status) === TICKET_STATUS.COMPLETED &&
              dayjs.unix(a.sessionEnd).format("h A") === hour
          );

          const completedWaitTime = completedTickets.reduce(
            (acc, a) => acc + (a.sessionStart - a.created),
            0
          );

          const totalWaitTime = incompleteWaitTime + completedWaitTime;
          const total =
            (incompletedTickets?.length || 0) +
            (completedTickets?.length || 0);
          const hourlyAverage = Math.ceil(totalWaitTime / total) || null;

          return {
            hour,
            total,
            hourlyAverage: Number.isFinite(hourlyAverage)
              ? hourlyAverage
              : null,
          };
        })
        .filter(Boolean);

      const validPoints = chart.filter((a) => a.hourlyAverage !== null);
      const totalTickets = chart.reduce((a, b) => a + b.total, 0) || 0;
      const totalWaitTime = chart.reduce((acc, a) => {
        return acc + ((a.hourlyAverage || 0) * a.total);
      }, 0);
      const average =
        validPoints.length > 0
          ? chart.reduce((a, b) => a + b.hourlyAverage, 0) /
            validPoints.length
          : 0;
      const min =
        validPoints.length > 0
          ? Math.min(...validPoints.map((a) => a.hourlyAverage))
          : 0;
      const max =
        validPoints.length > 0
          ? Math.max(...validPoints.map((a) => a.hourlyAverage))
          : 0;

      return {
        serviceId: service.id,
        serviceName: service.name,
        totalTickets,
        totalWaitTime,
        average,
        min,
        max,
        chart,
      };
    })
    .sort((a, b) => {
      if (b.totalWaitTime - a.totalWaitTime !== 0) {
        return b.totalWaitTime - a.totalWaitTime;
      }
      return a.serviceName.localeCompare(b.serviceName);
    });
  const summary = buildRealtimeDurationSummary({
    rows: servicesData,
    scopeLabel: "Service",
    durationLabel: "Waiting Time",
    formatDuration: formatTime,
  });

  return (
    <Box>
      <Heading fontSize="24px" color="var(--black-dry)" mb="20px">
        Entire Organization
      </Heading>
      <EnterpriseReportSummary
        title="Realtime Service Waiting Time Summary"
        kpis={summary.kpis}
        tables={summary.tables}
        notes={summary.notes}
      />

      {servicesData.map((svc, index) => (
        <Box className="reportContainer" mt="20px" key={svc.serviceId}>
          <Heading fontSize="18px" color="var(--black-dry)" mb="10px">
            {index + 1}. {svc.serviceName}
          </Heading>
          {svc.totalTickets > 0 ? (
            <>
              <Table.Root className="stripedTable" size="sm" mb="20px">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader borderRight="1px solid #dadce0">
                      Total Tickets
                    </Table.ColumnHeader>
                    <Table.ColumnHeader borderRight="1px solid #dadce0">
                      Total Waiting Time
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>Avg /ticket</Table.ColumnHeader>
                    <Table.ColumnHeader>Avg /hour</Table.ColumnHeader>
                    <Table.ColumnHeader>Min.</Table.ColumnHeader>
                    <Table.ColumnHeader>Max</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell borderRight="1px solid #dadce0">
                      <Text color="var(--blue)">{svc.totalTickets}</Text>
                    </Table.Cell>
                    <Table.Cell borderRight="1px solid #dadce0">
                      <Text color="var(--blue)">
                        {formatTime(svc.totalWaitTime)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text>
                        {svc.totalTickets > 0
                          ? formatTime(svc.totalWaitTime / svc.totalTickets)
                          : "NA"}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text>
                        {Number.isFinite(svc.average)
                          ? formatTime(svc.average)
                          : "NA"}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text>
                        {svc.min > 0 ? formatTime(svc.min) : "NA"}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text>
                        {svc.max > 0 ? formatTime(svc.max) : "NA"}
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell colSpan={6} p={0}>
                      <Box mt="20px" mb="20px">
                        <AgentServiceTimePerServiceLineGraph
                          chart={svc.chart}
                          lineName="Avg Wait Time"
                          title={`${svc.serviceName}: Hour by Hour Comparison`}
                        />
                      </Box>
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table.Root>
            </>
          ) : (
            <Text fontSize="sm" mb="20px">
              No data available for this service
            </Text>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default OrganizationAverageWaitTimePerService;
